import { useEffect, useState } from "react";
import { Sparkles, Check, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

const TYPE_LABELS = { fine: "Fine", fixture_change: "Fixture change", rule_info: "Rule / league info", referee_info: "Referee info", meeting: "Meeting", registration: "Registration", other: "Other" };
const ASSIGNEE_LABELS = { treasurer: "Treasurer", secretary: "Club Secretary", coach: "Lead coach", admin: "Admin" };

export function InboxAnalysis({ item, onChanged }) {
  const [busy, setBusy] = useState(false);
  const a = item.analysis;
  const analyse = async () => {
    setBusy(true);
    try { await api.post(`/admin/inbox/${item.id}/analyse`); toast.success("Read and summarised"); onChanged(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (!a) return (
    <Button size="sm" variant="secondary" onClick={analyse} disabled={busy} data-testid="inbox-analyse-btn"><Sparkles className="w-3.5 h-3.5 mr-1" />{busy ? "Reading…" : "Read & propose actions"}</Button>
  );
  const facts = Object.entries(a.facts || {}).filter(([, v]) => v !== null && v !== "" && v !== undefined);
  return (
    <div className="rounded-lg border border-[#83B68B]/40 bg-[#83B68B]/5 p-3 space-y-3" data-testid="inbox-analysis">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full bg-[#83B68B] text-black font-bold" data-testid="inbox-analysis-type">{TYPE_LABELS[a.type] || a.type}</span>
        <span className="text-xs opacity-60">AI summary · {a.analysed_at?.slice(0, 10)}</span>
        <button type="button" onClick={analyse} disabled={busy} className="text-xs underline opacity-60 hover:opacity-100 ml-auto" data-testid="inbox-reanalyse-btn">Re-read</button>
      </div>
      <p className="text-sm whitespace-pre-wrap" data-testid="inbox-analysis-summary">{a.summary}</p>
      {facts.length > 0 && (
        <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs" data-testid="inbox-analysis-facts">
          {facts.map(([k, v]) => <div key={k}><dt className="opacity-50 uppercase tracking-widest text-[9px]">{k.replace(/_/g, " ")}</dt><dd className="font-medium">{k === "amount" ? `£${v}` : String(v)}</dd></div>)}
        </dl>
      )}
      {a.type === "fixture_change" && <FixtureApply item={item} onChanged={onChanged} />}
      <div className="space-y-2">
        <div className="text-[10px] tracking-widest uppercase opacity-60">Proposed actions — nothing happens until you approve</div>
        {(a.actions || []).length === 0 && <div className="text-xs opacity-60">No actions suggested.</div>}
        {(a.actions || []).map(act => <ActionRow key={act.id} item={item} act={act} onChanged={onChanged} />)}
      </div>
    </div>
  );
}

function FixtureApply({ item, onChanged }) {
  const [cands, setCands] = useState(null);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  const facts = item.analysis?.facts || {};
  const applied = item.analysis?.fixture_applied;
  useEffect(() => { if (!applied) api.get(`/admin/inbox/${item.id}/fixture-match`).then(r => { setCands(r.data.candidates); if (r.data.candidates[0]) setPick(r.data.candidates[0].id); }).catch(() => setCands([])); }, [item.id, applied]);
  const apply = async () => {
    if (!window.confirm("Move this booking to the new date/time? The coach and referee will get the usual amendment emails.")) return;
    setBusy(true);
    try { await api.post(`/admin/inbox/${item.id}/apply-fixture`, { booking_id: pick }); toast.success("Booking moved"); onChanged(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (applied) return <div className="rounded border border-[#83B68B]/50 bg-[#83B68B]/10 p-2.5 text-xs" data-testid="fixture-applied">Booking moved {applied.from} → {applied.to} by {applied.by}</div>;
  return (
    <div className="rounded border border-sky-500/40 bg-sky-500/10 p-2.5 space-y-2" data-testid="fixture-apply">
      <div className="text-[10px] tracking-widest uppercase text-sky-300">Apply fixture change</div>
      <div className="text-xs">New: <b>{facts.new_date || "(date unchanged)"}</b> {facts.new_time ? <>at <b>{facts.new_time}</b></> : "(time unchanged)"}</div>
      {cands === null ? <div className="text-xs opacity-60">Finding the booking…</div>
        : cands.length === 0 ? <div className="text-xs opacity-60" data-testid="fixture-no-match">No matching booking found for “{facts.team || "this team"}” — amend it from the calendar.</div>
        : <div className="flex flex-wrap gap-2 items-center">
            <select value={pick} onChange={e => setPick(e.target.value)} className="bg-background border border-border rounded px-2 py-1 text-xs max-w-full" data-testid="fixture-candidate">
              {cands.map(c => <option key={c.id} value={c.id}>{c.booking_date} {String(c.start_hour).padStart(2, "0")}:{String(c.start_min || 0).padStart(2, "0")} · {c.team_name}{c.opponent ? ` v ${c.opponent}` : ""} · {c.pitch_name}</option>)}
            </select>
            <Button size="sm" onClick={apply} disabled={busy || !pick || (!facts.new_date && !facts.new_time)} data-testid="fixture-apply-btn">{busy ? "Moving…" : "Move booking"}</Button>
          </div>}
    </div>
  );
}

function ActionRow({ item, act, onChanged }) {
  const [edit, setEdit] = useState(false);
  const [title, setTitle] = useState(act.title);
  const [due, setDue] = useState(act.due_date || "");
  const [busy, setBusy] = useState(false);
  const decide = async (decision) => {
    setBusy(true);
    try {
      await api.post(`/admin/inbox/${item.id}/actions/${act.id}`, { decision, ...(decision === "approve" ? { title, due_date: due || null } : {}) });
      toast.success(decision === "approve" ? "Task created" : "Skipped"); onChanged();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const done = act.status !== "proposed";
  return (
    <div className={`rounded border p-2.5 text-sm ${done ? "opacity-60 border-border" : "border-border bg-card"}`} data-testid={`inbox-action-${act.id}`}>
      <div className="flex flex-wrap items-start gap-2">
        <div className="flex-1 min-w-0">
          {edit && !done ? <input value={title} onChange={e => setTitle(e.target.value)} className="w-full bg-background border border-border rounded px-2 py-1 text-sm" data-testid={`inbox-action-title-${act.id}`} />
            : <div className="font-semibold">{title}</div>}
          <div className="text-xs opacity-70 mt-0.5 whitespace-pre-wrap">{act.details}</div>
          <div className="text-[10px] tracking-widest uppercase opacity-60 mt-1 flex flex-wrap gap-2">
            <span>→ {ASSIGNEE_LABELS[act.assignee] || act.assignee}{(act.assignee_ids || []).length === 0 ? " (no one holds this role — assign in Task Board)" : ""}</span>
            <span>· {act.priority}</span>
            {edit && !done ? <input type="date" value={due} onChange={e => setDue(e.target.value)} className="bg-background border border-border rounded px-1 text-[10px]" data-testid={`inbox-action-due-${act.id}`} /> : due && <span>· due {due}</span>}
            {done && <span className={act.status === "approved" ? "text-[#83B68B]" : ""}>· {act.status}{act.task_id ? " → task created" : ""}</span>}
          </div>
        </div>
        {!done && (
          <div className="flex gap-1 shrink-0">
            <Button size="sm" onClick={() => decide("approve")} disabled={busy} data-testid={`inbox-action-approve-${act.id}`}><Check className="w-3.5 h-3.5 mr-1" />Approve</Button>
            <Button size="sm" variant="ghost" onClick={() => setEdit(v => !v)} data-testid={`inbox-action-edit-${act.id}`}>{edit ? "Done" : "Edit"}</Button>
            <Button size="sm" variant="ghost" onClick={() => decide("skip")} disabled={busy} data-testid={`inbox-action-skip-${act.id}`}><X className="w-3.5 h-3.5" /></Button>
          </div>
        )}
      </div>
    </div>
  );
}
